import { useEffect, useState, type ReactNode } from "react";

import { useAccounts } from "../../api/hooks/useAccounts";
import { useTags } from "../../api/hooks/useTags";
import type { GlobalFilters } from "../../lib/globalFilters";
import { cn } from "../../lib/cn";

const INPUT_CLASS =
  "rounded-control border border-outline-variant bg-surface-container px-2 py-1.5 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-focus-ring focus:border-focus-ring";

function FilterChip({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-outline-variant bg-surface px-2 py-0.5 text-xs text-on-surface">
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label} filter`}
        className="leading-none text-on-surface-muted transition-colors hover:text-on-surface"
      >
        ×
      </button>
    </span>
  );
}

// Self-contained so the accounts query only fires on pages that show the
// control (selecting "All accounts" clears the filter).
function AccountSelect({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (next: string | undefined) => void;
}) {
  const accounts = useAccounts();
  const items = accounts.data?.items ?? [];
  return (
    <select
      aria-label="Account"
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value || undefined)}
      className={INPUT_CLASS}
    >
      <option value="">All accounts</option>
      {items.map((account) => (
        <option key={account.account_id} value={account.account_id}>
          {account.name}
        </option>
      ))}
    </select>
  );
}

function TagSelect({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (next: string | undefined) => void;
}) {
  const tags = useTags();
  const items = tags.data?.items ?? [];
  return (
    <select
      aria-label="Tag"
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value || undefined)}
      className={INPUT_CLASS}
    >
      <option value="">All tags</option>
      {items.map((tag) => (
        <option key={tag.tag_id} value={tag.tag_id}>
          {tag.name}
        </option>
      ))}
    </select>
  );
}

export interface FilterBarProps {
  filters: GlobalFilters;
  setFilters: (patch: Partial<GlobalFilters>) => void;
  /** Render the account selector ("All accounts" clears it). */
  showAccount?: boolean;
  /** Render the tag selector ("All tags" clears it). */
  showTag?: boolean;
  /** Render the custom date-range inputs + since/until chips. */
  showDateRange?: boolean;
  /** Optional leading slot (e.g. the dashboard's date-range preset chips). */
  leading?: ReactNode;
  className?: string;
  "aria-label"?: string;
}

// Embedded, page-scoped filter bar (replaces the former site-wide toolbar
// filters). Each page renders only the controls it supports and owns its own
// state via usePageFilters, so clearing actually clears -- nothing re-injects a
// dropped param.
export function FilterBar({
  filters,
  setFilters,
  showAccount = false,
  showTag = false,
  showDateRange = false,
  leading,
  className,
  "aria-label": ariaLabel = "Filters",
}: FilterBarProps) {
  // Date inputs are staged locally so the operator can pick a full range and
  // apply it in one navigation instead of refetching on every keystroke.
  const [sinceDraft, setSinceDraft] = useState(filters.since ?? "");
  const [untilDraft, setUntilDraft] = useState(filters.until ?? "");
  useEffect(() => {
    setSinceDraft(filters.since ?? "");
  }, [filters.since]);
  useEffect(() => {
    setUntilDraft(filters.until ?? "");
  }, [filters.until]);
  const datesDirty =
    sinceDraft !== (filters.since ?? "") || untilDraft !== (filters.until ?? "");

  const applyDates = () => {
    setFilters({
      since: sinceDraft || undefined,
      until: untilDraft || undefined,
    });
  };

  const dateActive =
    showDateRange &&
    (filters.since !== undefined || filters.until !== undefined);
  const anyActive =
    dateActive ||
    (showAccount && filters.account_id !== undefined) ||
    (showTag && filters.tag_id !== undefined);

  const clearAll = () => {
    setFilters({
      ...(showDateRange ? { since: undefined, until: undefined } : {}),
      ...(showAccount ? { account_id: undefined } : {}),
      ...(showTag ? { tag_id: undefined } : {}),
    });
  };

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn("flex flex-col gap-2", className)}
    >
      <div className="flex flex-wrap items-center gap-2">
        {leading}

        {showAccount ? (
          <AccountSelect
            value={filters.account_id}
            onChange={(next) => setFilters({ account_id: next })}
          />
        ) : null}

        {showTag ? (
          <TagSelect
            value={filters.tag_id}
            onChange={(next) => setFilters({ tag_id: next })}
          />
        ) : null}

        {showDateRange ? (
          <div className="flex items-center gap-2">
            <input
              aria-label="Start date"
              type="date"
              value={sinceDraft}
              onChange={(event) => setSinceDraft(event.target.value)}
              className={INPUT_CLASS}
            />
            <span aria-hidden="true" className="text-on-surface-muted">
              –
            </span>
            <input
              aria-label="End date"
              type="date"
              value={untilDraft}
              onChange={(event) => setUntilDraft(event.target.value)}
              className={INPUT_CLASS}
            />
            {datesDirty ? (
              <button
                type="button"
                onClick={applyDates}
                className="rounded-control bg-primary px-3 py-1.5 text-sm font-medium text-on-primary transition-colors hover:bg-primary-hover"
              >
                Apply
              </button>
            ) : null}
          </div>
        ) : null}

        {anyActive ? (
          <button
            type="button"
            onClick={clearAll}
            className="text-sm font-medium text-on-surface-variant underline hover:text-on-surface"
          >
            Clear all
          </button>
        ) : null}
      </div>

      {dateActive ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-on-surface-muted">
            Active filters
          </span>
          {filters.since !== undefined ? (
            <FilterChip
              label={`From ${filters.since}`}
              onRemove={() => setFilters({ since: undefined })}
            />
          ) : null}
          {filters.until !== undefined ? (
            <FilterChip
              label={`To ${filters.until}`}
              onRemove={() => setFilters({ until: undefined })}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
